import type { ReactNode } from "react";
import {
  AlignJustify,
  Layers,
  Type,
  Settings as SettingsIcon,
  Square,
  Users,
  PenTool,
  AlignHorizontalJustifyCenter,
} from "lucide-react";

export type OutlinePanel =
  | "body"
  | "simple"
  | "header"
  | "top-left"
  | "top-right"
  | "footer" // bottom-left margin
  | "margins"
  | "countersigner"
  | "signature-block"
  | "settings";

export interface OutlineOverrides {
  header: boolean;
  topLeft: boolean;
  topRight: boolean;
  footer: boolean; // bottom-left margin
  margins: boolean;
  countersigner: boolean;
  signatureBlock: boolean;
}

interface OutlineItemProps {
  label: string;
  icon: ReactNode;
  active: boolean;
  badge?: string;
  onClick: () => void;
}

function OutlineItem({ label, icon, active, badge, onClick }: OutlineItemProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center justify-between w-full text-left px-3 py-2 rounded-md text-[12.5px] transition-colors border-l-4 ${
        active
          ? "bg-tide-ss text-tide font-medium border-l-tide"
          : "text-ink-3 hover:bg-canvas/40 border-l-transparent"
      }`}
    >
      <span className="flex items-center gap-2">
        {icon}
        <span>{label}</span>
      </span>
      {badge && (
        <span className="bg-spark-s text-spark text-[9.5px] font-medium px-1.5 rounded-full">
          {badge}
        </span>
      )}
    </button>
  );
}

interface OutlineRailProps {
  active: OutlinePanel;
  onSelect: (panel: OutlinePanel) => void;
  overrides: OutlineOverrides;
}

export function OutlineRail({
  active,
  onSelect,
  overrides,
}: OutlineRailProps) {
  return (
    <aside
      className="w-[200px] flex-shrink-0 hair-r border-r bg-paper sticky self-start"
      style={{
        top: "calc(var(--banner-h) + 3.5rem)",
        height: "calc(100vh - var(--banner-h) - 3.5rem)",
        overflowY: "auto",
      }}
    >
      <div className="px-4 py-3 hair-b border-b">
        <div className="text-[10.5px] text-ink-4 uppercase tracking-wider font-semibold">
          Outline
        </div>
      </div>

      <nav className="p-2 space-y-0.5">
        <div className="px-2 py-1.5 text-ink-5 text-[10.5px] uppercase tracking-wider font-semibold">
          Content
        </div>
        <OutlineItem
          label="Full agreement"
          icon={<AlignJustify size={12} strokeWidth={1.8} />}
          active={active === "body"}
          onClick={() => onSelect("body")}
        />
        <OutlineItem
          label="Simple terms"
          icon={<AlignJustify size={12} strokeWidth={1.8} />}
          active={active === "simple"}
          onClick={() => onSelect("simple")}
        />

        <div className="px-2 py-1.5 mt-3 text-ink-5 text-[10.5px] uppercase tracking-wider font-semibold flex items-center justify-between">
          <span>Design</span>
          <Layers size={11} strokeWidth={1.6} className="text-ink-5" />
        </div>
        <OutlineItem
          label="Header"
          icon={<Type size={12} strokeWidth={1.8} />}
          active={active === "header"}
          badge={overrides.header ? "Override" : undefined}
          onClick={() => onSelect("header")}
        />
        <OutlineItem
          label="Top-left margin"
          icon={<AlignHorizontalJustifyCenter size={12} strokeWidth={1.8} />}
          active={active === "top-left"}
          badge={overrides.topLeft ? "Override" : undefined}
          onClick={() => onSelect("top-left")}
        />
        <OutlineItem
          label="Top-right margin"
          icon={<AlignHorizontalJustifyCenter size={12} strokeWidth={1.8} />}
          active={active === "top-right"}
          badge={overrides.topRight ? "Override" : undefined}
          onClick={() => onSelect("top-right")}
        />
        <OutlineItem
          label="Bottom-left margin"
          icon={<AlignHorizontalJustifyCenter size={12} strokeWidth={1.8} />}
          active={active === "footer"}
          badge={overrides.footer ? "Override" : undefined}
          onClick={() => onSelect("footer")}
        />
        <OutlineItem
          label="Margins"
          icon={<Square size={12} strokeWidth={1.8} />}
          active={active === "margins"}
          badge={overrides.margins ? "Override" : undefined}
          onClick={() => onSelect("margins")}
        />
        <OutlineItem
          label="Countersigner"
          icon={<Users size={12} strokeWidth={1.8} />}
          active={active === "countersigner"}
          badge={overrides.countersigner ? "Override" : undefined}
          onClick={() => onSelect("countersigner")}
        />
        <OutlineItem
          label="Signature block"
          icon={<PenTool size={12} strokeWidth={1.8} />}
          active={active === "signature-block"}
          badge={overrides.signatureBlock ? "Override" : undefined}
          onClick={() => onSelect("signature-block")}
        />

        <div className="px-2 py-1.5 mt-3 text-ink-5 text-[10.5px] uppercase tracking-wider font-semibold">
          Meta
        </div>
        <OutlineItem
          label="Identity & status"
          icon={<SettingsIcon size={12} strokeWidth={1.8} />}
          active={active === "settings"}
          onClick={() => onSelect("settings")}
        />
      </nav>
    </aside>
  );
}
